/**
 * Campos vazios em documentos de licitação: padronizar "Não informado".
 */
export function displayField(value, fallback = "Não informado") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") {
    const t = value.trim();
    return t.length === 0 ? fallback : value;
  }
  return String(value);
}

/**
 * Texto corrido: quebra preferencialmente entre palavras (evita corte no meio da palavra).
 * line-height na faixa 1,35–1,5.
 */
export const prosePdfClass =
  "text-justify leading-[1.4] [word-break:normal] [overflow-wrap:break-word] hyphens-none sm:leading-[1.45]";

/** Largura máxima confortável para leitura (usa mais a largura do cartão em telas largas) */
export const propostaMaxReadableClass = "max-w-[min(100%,90ch)]";

/** Cartões / blocos — altura automática */
export const propostaCardClass =
  "rounded-xl border border-slate-300/90 bg-white p-5 shadow-sm sm:p-6 min-h-0 min-w-0 overflow-visible";

/** Títulos de seção */
export const propostaTituloSecaoClass =
  "mb-4 border-b border-slate-200 pb-2 text-[10pt] font-bold uppercase tracking-wide text-slate-700";

/** Espaçamento entre blocos principais */
export const propostaBlocoGapClass = "mb-10";

/** Detecta coluna de unidade (planilhas variam id/nome). */
function isUnidadeColumn(col) {
  const id = String(col.id || "").toLowerCase();
  const nome = String(col.nome || "").toLowerCase();
  return (
    id === "unidade" ||
    id === "und" ||
    nome === "und" ||
    nome.includes("unidade")
  );
}

/** Coluna de texto longo / descrição (id, tipo ou nome). */
export function isPropostaColunaDescricao(col) {
  if (!col) return false;
  const id = String(col.id || "").toLowerCase();
  const nome = String(col.nome || "").toLowerCase();
  return (
    col.tipo === "texto_longo" ||
    id === "descricao" ||
    id.includes("descricao") ||
    nome.includes("descri") ||
    nome.includes("descr.")
  );
}

/**
 * Larguras relativas ao nº de colunas (planilhas largas não podem forçar a tabela fora da “folha”).
 * @param {number} [totalColumns=6]
 */
function propostaTabelaDensity(totalColumns = 6) {
  const n = Math.max(1, totalColumns);
  return {
    /** Descrição: mais largura + padding horizontal (textos longos); outras colunas compensam. */
    descClass:
      n >= 11
        ? "w-[23%] px-1.5 sm:px-2"
        : n >= 8
          ? "w-[30%] sm:w-[32%] px-2 sm:px-2.5"
          : "w-[40%] sm:w-[44%] px-2.5 sm:px-3.5",
    valorClass:
      n >= 11
        ? "w-[8%] min-w-0 max-w-[4.5rem] px-1"
        : n >= 8
          ? "w-[9%] min-w-0 max-w-[5rem] px-1 sm:px-1.5"
          : "w-[12%] min-w-0 max-w-[5.75rem] px-1.5 sm:px-2",
    unidadeClass:
      n >= 8
        ? "w-[6%] min-w-0 max-w-[3.75rem] px-1 whitespace-nowrap"
        : "w-[7%] min-w-0 max-w-[4.75rem] px-1.5 whitespace-nowrap",
    itemClass:
      n >= 8
        ? "w-[3.5%] min-w-0 max-w-[2.1rem] px-1"
        : "w-[4.5%] min-w-0 max-w-[2.5rem] px-1.5",
    qtdClass:
      n >= 8
        ? "w-[5.5%] min-w-0 max-w-[3.1rem] px-1"
        : "w-[7%] min-w-0 max-w-[3.75rem] px-1.5",
    numeroClass:
      n >= 8
        ? "w-[5.5%] min-w-0 max-w-[3.1rem] px-1"
        : "w-[6.5%] min-w-0 max-w-[3.5rem] px-1.5",
    textoCurtoClass:
      n >= 8
        ? "w-[5.5%] min-w-0 max-w-[3.25rem] px-1"
        : "w-[8%] min-w-0 max-w-[5rem] px-1.5",
  };
}

/**
 * Classes por coluna — respeita a largura da folha; use totalColumns = colunas visíveis.
 */
export function getPropostaColunaTabelaClass(col, totalColumns = 6) {
  const id = String(col.id || "").toLowerCase();
  const nome = String(col.nome || "").toLowerCase();
  const d = propostaTabelaDensity(totalColumns);

  if (isPropostaColunaDescricao(col)) {
    return ["proposta-celula-descricao", d.descClass, "align-top"].join(" ");
  }

  if (isUnidadeColumn(col)) {
    return ["proposta-celula-unidade", d.unidadeClass, "align-top text-center"].join(" ");
  }

  if (id === "item" || nome === "item") {
    return [d.itemClass, "align-top", "text-center"].join(" ");
  }

  if (
    col.tipo === "numero" &&
    (id === "quantidade" || id === "qtd" || nome.includes("quant"))
  ) {
    return [d.qtdClass, "align-top", "text-center"].join(" ");
  }

  if (col.tipo === "valor" || col.tipo === "calculada") {
    return ["proposta-cell-valor", d.valorClass, "align-top", "text-right"].join(" ");
  }

  if (col.tipo === "numero") {
    return [d.numeroClass, "align-top", "text-center"].join(" ");
  }

  return [d.textoCurtoClass, "align-top", "text-center"].join(" ");
}

/**
 * Peso relativo no <colgroup> (mesma detecção de `getPropostaColunaTabelaClass`).
 * Descrição maior que as outras, mas sem ~57% (espremia colunas valor com whitespace-nowrap).
 */
function getPropostaColunaPeso(col) {
  const id = String(col.id || "").toLowerCase();
  const nome = String(col.nome || "").toLowerCase();

  if (isPropostaColunaDescricao(col)) {
    return 56;
  }
  if (isUnidadeColumn(col)) {
    return 15;
  }
  if (id === "item" || nome === "item") {
    return 12;
  }
  if (
    col.tipo === "numero" &&
    (id === "quantidade" || id === "qtd" || nome.includes("quant"))
  ) {
    return 17;
  }
  if (col.tipo === "valor" || col.tipo === "calculada") {
    return 30;
  }
  if (col.tipo === "numero") {
    return 15;
  }
  return 16;
}

/**
 * Percentuais por coluna para `<colgroup>/<col>`; soma = 100%.
 * Com `table-fixed`, se a 1ª linha for grupo com `colspan`, larguras nas `th`/`td` não definem colunas —
 * o navegador reparte igual; o colgroup fixa a proporção (descrição maior).
 */
export function getPropostaColunasWidthsPercent(colunas) {
  if (!colunas?.length) return [];
  const weights = colunas.map((c) => getPropostaColunaPeso(c));
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) {
    const eq = 100 / colunas.length;
    return colunas.map(() => `${eq}%`);
  }
  return weights.map((w) => `${(w / sum) * 100}%`);
}

/**
 * Largura mínima (px) da tabela de itens na visualização: evita sobreposição em valores (nowrap)
 * quando o viewport é estreito; o wrapper usa overflow-x-auto.
 */
export function getPropostaTabelaItensMinWidthPx(totalColumns = 6) {
  const n = Math.max(1, totalColumns);
  if (n >= 11) return 1080;
  if (n >= 8) return 960;
  return 780;
}

/**
 * Colunas iniciais da linha de subtotal que devem ser fundidas com o rótulo
 * "Subtotal do grupo" (evita texto longo na coluna Item estreita).
 * Inclui a coluna 0 e todas as colunas texto/texto_longo consecutivas até a primeira coluna numérica/valor.
 */
export function getSubtotalLabelColSpan(colunas) {
  if (!colunas?.length) return 1;
  let span = 0;
  for (let i = 0; i < colunas.length; i++) {
    const col = colunas[i];
    const isLabelPart =
      i === 0 || col.tipo === "texto" || col.tipo === "texto_longo";
    if (isLabelPart) span += 1;
    else break;
  }
  return Math.max(1, span);
}

/**
 * Cabeçalhos da tabela de itens.
 * Na tela, nowrap em colunas estreitas faz títulos se sobreporem; impressão costuma ter largura suficiente.
 */
export function getPropostaThHeaderClass(col) {
  if (isPropostaColunaDescricao(col)) {
    return "whitespace-normal min-w-0 [overflow-wrap:anywhere]";
  }
  return "whitespace-normal min-w-0 [overflow-wrap:anywhere] leading-tight hyphens-none";
}

/** Estilos inline por coluna no <colgroup> (largura % + mínimos nas colunas compactas). */
export function getPropostaColElementStyle(col, widthPercent) {
  const style = { width: widthPercent };
  if (isPropostaColunaDescricao(col)) {
    return style;
  }
  if (col.tipo === "valor" || col.tipo === "calculada") {
    style.minWidth = "5.75rem";
  } else if (
    col.tipo === "numero" &&
    (String(col.id || "").toLowerCase() === "quantidade" ||
      String(col.id || "").toLowerCase() === "qtd" ||
      String(col.nome || "").toLowerCase().includes("quant"))
  ) {
    style.minWidth = "4.25rem";
  } else if (isUnidadeColumn(col)) {
    style.minWidth = "4rem";
  } else if (String(col.id || "").toLowerCase() === "item" || String(col.nome || "").toLowerCase() === "item") {
    style.minWidth = "2.5rem";
  }
  return style;
}
